import React, { useState } from 'react';
import { Car, RefreshCw, AlertCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { matchStock } from '../../services/aiService';

export default function AIStockMatcherCard({ lead }) {
  const [matches, setMatches] = useState([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [error, setError] = useState('');
  const [isCollapsed, setIsCollapsed] = useState(false);

  const handleMatch = async (e) => {
    if (e) e.stopPropagation();
    if (!lead?.id) return;
    setLoading(true);
    setError('');
    try {
      const res = await matchStock(lead.id);
      if (res.success) {
        setMatches(res.matches || []);
        setHasSearched(true);
      } else {
        setError(res.message || 'Failed to match stock.');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Error matching vehicle stock');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="ld-card">
      <div className="ld-card-header" onClick={() => setIsCollapsed(!isCollapsed)}>
        <div className="ld-card-header-left">
          <Car size={16} className="ld-card-icon" style={{ color: '#38bdf8' }} />
          <span className="ld-card-title">SMART AI STOCK MATCHER</span>
          <span style={{
            fontSize: '11px',
            padding: '2px 8px',
            borderRadius: '9999px',
            background: 'rgba(56, 189, 248, 0.15)',
            color: '#38bdf8',
            fontWeight: '600',
            border: '1px solid rgba(56, 189, 248, 0.3)'
          }}>
            Inventory AI
          </span>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <button
            onClick={handleMatch}
            disabled={loading}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              fontSize: '12px',
              fontWeight: '600',
              padding: '4px 10px',
              borderRadius: '6px',
              background: '#0284c7',
              color: '#ffffff',
              border: 'none',
              cursor: loading ? 'not-allowed' : 'pointer',
              opacity: loading ? 0.7 : 1,
              transition: 'all 0.15s ease'
            }}
          >
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
            <span>{loading ? 'Matching...' : hasSearched ? 'Re-Match Stock' : 'Match Inventory'}</span>
          </button>
          <button className="ld-collapse-btn">
            {isCollapsed ? <ChevronDown size={16} /> : <ChevronUp size={16} />}
          </button>
        </div>
      </div>

      {!isCollapsed && (
        <div className="ld-card-body">
          {error && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '12px',
              color: '#f87171',
              background: 'rgba(239, 68, 68, 0.1)',
              padding: '8px 12px',
              borderRadius: '8px',
              border: '1px solid rgba(239, 68, 68, 0.2)'
            }}>
              <AlertCircle size={14} />
              <span>{error}</span>
            </div>
          )}

          {loading ? (
            <div style={{ padding: '24px 0', textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '8px' }}>
              <div className="ld-skeleton-pulse" style={{ width: 24, height: 24, borderRadius: '50%' }} />
              <span style={{ fontSize: '12px', color: 'var(--muted)' }}>Scanning inventory with AI...</span>
            </div>
          ) : hasSearched && matches.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {matches.map((item, idx) => (
                <div
                  key={idx}
                  style={{
                    padding: '12px 14px',
                    borderRadius: '8px',
                    border: '1px solid var(--border)',
                    background: 'var(--surface-2)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '12px'
                  }}
                >
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontWeight: '700', fontSize: '13px', color: 'var(--text)' }}>
                        {item.year} {item.make} {item.model}
                      </span>
                      {item.color && (
                        <span style={{ fontSize: '11px', padding: '1px 6px', borderRadius: '4px', background: 'var(--surface-3)', color: 'var(--text-2)' }}>
                          {item.color}
                        </span>
                      )}
                      <span style={{
                        fontSize: '11px',
                        fontWeight: '700',
                        padding: '1px 8px',
                        borderRadius: '9999px',
                        background: 'rgba(16, 185, 129, 0.15)',
                        color: '#10b981',
                        border: '1px solid rgba(16, 185, 129, 0.3)'
                      }}>
                        {item.matchScore}% Match
                      </span>
                    </div>
                    <div style={{ fontSize: '12px', color: 'var(--text-2)' }}>
                      {item.reasoning}
                    </div>
                  </div>

                  <div style={{ fontSize: '13px', fontWeight: '800', color: 'var(--text)', whiteSpace: 'nowrap' }}>
                    {item.price ? `$${Number(item.price).toLocaleString()}` : 'Price on request'}
                  </div>
                </div>
              ))}
            </div>
          ) : hasSearched && matches.length === 0 ? (
            <div style={{ padding: '12px', textAlign: 'center', fontSize: '12px', color: 'var(--muted)', background: 'var(--surface-2)', borderRadius: '8px' }}>
              No direct stock match found for this lead budget/preference.
            </div>
          ) : (
            <div style={{
              padding: '16px',
              textAlign: 'center',
              background: 'var(--surface-2)',
              borderRadius: '8px',
              border: '1px dashed var(--border)',
              color: 'var(--muted)',
              fontSize: '12px'
            }}>
              <Car size={24} style={{ margin: '0 auto 6px', opacity: 0.5 }} />
              <div>Click <strong>"Match Inventory"</strong> to rank best available vehicles in stock for this customer.</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
